import { ParseResult } from "papaparse";

import {
    MisoPercentageReduction,
    ProjectSizeAdjustmentType,
    ScenarioAssumptionsCSV,
    ScenarioAssumptionsCSVFields
} from "in_queue/types/scenarioType";
import { resolveProjectAssumption } from "./projectAssumption";

interface ParseCsvResultsParams {
    readonly results: ParseResult<ScenarioAssumptionsCSV>;
    readonly onlyExclusionSupported: boolean;
    readonly validProjectIds: Set<string>;
    readonly speculativeProjectIds: Set<string>;
}

export interface ParseCsvOutput {
    readonly assumptions: Record<string, MisoPercentageReduction>;
    readonly includedSpeculativeProjectIds: ReadonlyArray<string>;
    readonly honoredProjectIds: ReadonlyArray<string>;
    readonly warnings: ReadonlyArray<string>;
    readonly headerError: string | undefined;
}

export const parseCsvResults = ({
    results,
    onlyExclusionSupported,
    validProjectIds,
    speculativeProjectIds
}: ParseCsvResultsParams): ParseCsvOutput => {
    const expectedFields = onlyExclusionSupported
        ? [ScenarioAssumptionsCSVFields.project_id]
        : [
              ScenarioAssumptionsCSVFields.project_id,
              ScenarioAssumptionsCSVFields.eris_percentage_reduction,
              ScenarioAssumptionsCSVFields.nris_percentage_reduction
          ];

    const actualFields = results.meta.fields ?? [];
    const missingFields = expectedFields.filter(
        (field) => !actualFields.includes(field)
    );

    if (missingFields.length > 0) {
        const missingFieldsText = missingFields
            .map((field) => `"${field}"`)
            .join(", ");
        return {
            assumptions: {},
            includedSpeculativeProjectIds: [],
            honoredProjectIds: [],
            warnings: [],
            headerError: `Uploaded CSV header is invalid. Missing required field${
                missingFields.length > 1 ? "s" : ""
            } ${missingFieldsText}. Extra columns are allowed.`
        };
    }

    const assumptions: Record<string, MisoPercentageReduction> = {};
    const includedSpeculativeProjectIds: string[] = [];
    const warnings: string[] = [];
    const projectIdsInCsv = new Set<string>();
    const honoredProjectIds = new Set<string>();

    results.data.forEach((item) => {
        const projectId = item[ScenarioAssumptionsCSVFields.project_id];
        if (!validProjectIds.has(projectId)) {
            warnings.push(projectId);
            return;
        }

        projectIdsInCsv.add(projectId);

        const isSpeculative = speculativeProjectIds.has(projectId);

        if (onlyExclusionSupported) {
            if (isSpeculative) {
                return;
            }

            assumptions[projectId] = {
                type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION,
                eris: 100,
                nris: 100
            };
            honoredProjectIds.add(projectId);
        } else {
            const erisValue =
                item[ScenarioAssumptionsCSVFields.eris_percentage_reduction];
            const eris =
                typeof erisValue === "string"
                    ? parseFloat(erisValue)
                    : erisValue;
            const nrisValue =
                item[ScenarioAssumptionsCSVFields.nris_percentage_reduction];
            const nris =
                typeof nrisValue === "string"
                    ? parseFloat(nrisValue)
                    : nrisValue;

            if (
                eris === undefined ||
                nris === undefined ||
                isNaN(eris) ||
                isNaN(nris)
            ) {
                warnings.push(projectId);
                return;
            }

            if (eris < 0 || eris > 100 || nris < 0 || nris > 100) {
                warnings.push(projectId);
                return;
            }

            const action = resolveProjectAssumption({
                eris,
                nris,
                isSpeculative
            });

            if (action.assumption !== undefined) {
                assumptions[projectId] = action.assumption;
            }

            if (action.includeInProjectAdditions) {
                includedSpeculativeProjectIds.push(projectId);
            }

            honoredProjectIds.add(projectId);
        }
    });

    speculativeProjectIds.forEach((spId) => {
        if (!projectIdsInCsv.has(spId)) {
            includedSpeculativeProjectIds.push(spId);
        }
    });

    return {
        assumptions,
        includedSpeculativeProjectIds,
        honoredProjectIds: Array.from(honoredProjectIds),
        warnings,
        headerError: undefined
    };
};
